import { ChangeEvent, FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createRequirement } from '../api/requirements';
import { AppShell } from '../components/AppShell';
import { SectionCard } from '../components/SectionCard';

const MAX_UPLOAD_MB = 25;
const MAX_FILES = 10;

export function Submit() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [customer, setCustomer] = useState('');
  const [project, setProject] = useState('');
  const [context, setContext] = useState('');
  const [files, setFiles] = useState<readonly File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      navigate('/inbox');
    },
  });

  const onFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (picked.length > MAX_FILES) {
      setFileError(`Maximal ${MAX_FILES} Dateien pro Upload.`);
      return;
    }
    const tooBig = picked.find((f) => f.size > MAX_UPLOAD_MB * 1024 * 1024);
    if (tooBig) {
      setFileError(`${tooBig.name} überschreitet ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setFiles(picked);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (fileError) return;
    mutation.mutate({
      title,
      description,
      customer: customer || undefined,
      project: project || undefined,
      context: context || undefined,
      files: files.length > 0 ? Array.from(files) : undefined,
    });
  };

  return (
    <AppShell crumbs={['Mindwright', 'Neue Anforderung']}>
      <div className="page" style={{ paddingTop: '2rem' }}>
        <SectionCard title="Neue Anforderung">
          <form onSubmit={onSubmit} noValidate>
            <div className="form-grid">
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-title">
                  Titel
                </label>
                <input
                  id="submit-title"
                  className="input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  minLength={3}
                />
              </div>
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-description">
                  Beschreibung
                </label>
                <textarea
                  id="submit-description"
                  className="textarea"
                  rows={6}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  minLength={10}
                />
              </div>
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-customer">
                  Kunde <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--fg-faint)' }}>(optional)</span>
                </label>
                <input
                  id="submit-customer"
                  className="input"
                  type="text"
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                />
              </div>
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-project">
                  Projekt <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--fg-faint)' }}>(optional)</span>
                </label>
                <input
                  id="submit-project"
                  className="input"
                  type="text"
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                />
              </div>
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-context">
                  Kontext <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--fg-faint)' }}>(optional)</span>
                </label>
                <textarea
                  id="submit-context"
                  className="textarea"
                  rows={4}
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  maxLength={5000}
                  placeholder="Softwaretyp, Tech-Stack/Framework, bestehendes oder neues Projekt, weitere Rahmenbedingungen – hilft der Aufbereitung, unnötige Rückfragen zu vermeiden."
                />
                <div className="field-hint">
                  Hintergrund für die KI-Aufbereitung. Beantwortet der Kontext eine Frage bereits, entfällt die Rückfrage.
                </div>
              </div>
              <div className="field field-full">
                <label className="field-label" htmlFor="submit-files">
                  Anhänge <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--fg-faint)' }}>(optional, max {MAX_FILES} · je {MAX_UPLOAD_MB} MB)</span>
                </label>
                <input
                  id="submit-files"
                  className="input"
                  type="file"
                  multiple
                  onChange={onFilesChange}
                />
                {files.length > 0 && (
                  <ul className="field-hint" style={{ paddingLeft: '1rem', margin: 0 }}>
                    {files.map((f) => (
                      <li key={f.name}>
                        {f.name} ({Math.round(f.size / 1024)} kB)
                      </li>
                    ))}
                  </ul>
                )}
                {fileError && <div className="field-error">{fileError}</div>}
              </div>
            </div>

            {mutation.isError && (
              <div className="alert-banner alert-error" style={{ marginTop: '1rem' }}>
                Fehler beim Absenden.
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn-ghost-app"
                onClick={() => navigate('/inbox')}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="btn-primary-app"
                disabled={mutation.isPending || !!fileError}
              >
                {mutation.isPending ? 'Sende…' : 'Absenden'}
              </button>
            </div>
          </form>
        </SectionCard>
      </div>
    </AppShell>
  );
}
